import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import Header from "./Header";
import { api } from "../server";

const STATUSES = ["Open", "In Progress", "Resolved", "Closed"];
const PRIORITIES = ["Low", "Medium", "High"];

const statusColor = (s) =>
  ({
    Open: "bg-warning-subtle text-warning",
    "In Progress": "bg-info-subtle text-info",
    Resolved: "bg-success-subtle text-success",
    Closed: "bg-secondary-subtle text-secondary",
  })[s] || "bg-light text-dark";

const priorityColor = (p) =>
  ({
    Low: "bg-success-subtle text-success",
    Medium: "bg-info-subtle text-info",
    High: "bg-danger-subtle text-danger",
  })[p] || "bg-light text-dark";

function SupportTickets() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [q, setQ] = useState("");

  const [opened, setOpened] = useState(null);
  const [response, setResponse] = useState("");
  const [status, setStatus] = useState("Open");
  const [priority, setPriority] = useState("Medium");
  const [saving, setSaving] = useState(false);
  const editorRef = useRef(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set("status", filterStatus);
      if (filterPriority) params.set("priority", filterPriority);
      const data = await api.get(
        `/api/admin/support/tickets?${params.toString()}`,
      );
      setRows(data?.tickets || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterPriority]);
  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter(
      (r) =>
        String(r.subject || "")
          .toLowerCase()
          .includes(query) ||
        String(r.message || "")
          .toLowerCase()
          .includes(query) ||
        String(r.customer?.name || "")
          .toLowerCase()
          .includes(query) ||
        String(r.customer?.phone || "")
          .toLowerCase()
          .includes(query),
    );
  }, [rows, q]);

  const openTicket = (t) => {
    setOpened(t);
    setResponse(t.response || "");
    setStatus(t.status || "Open");
    setPriority(t.priority || "Medium");
    setTimeout(
      () =>
        editorRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        }),
      50,
    );
  };
  const closeTicket = () => {
    setOpened(null);
    setResponse("");
    setStatus("Open");
    setPriority("Medium");
  };

  const saveTicket = async () => {
    if (!opened) return;
    setSaving(true);
    try {
      const payload = { status, priority };
      if (response.trim() && response.trim() !== (opened.response || "")) {
        payload.response = response.trim();
      }
      const data = await api.put(
        `/api/admin/support/tickets/${opened._id}`,
        payload,
      );
      const updated = data?.ticket;
      setRows((prev) =>
        prev.map((x) => (x._id === opened._id ? { ...x, ...updated } : x)),
      );
      closeTicket();
    } catch (err) {
      alert(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const stats = useMemo(() => {
    const total = rows.length;
    const open = rows.filter((r) => r.status === "Open").length;
    const inProg = rows.filter((r) => r.status === "In Progress").length;
    const resolved = rows.filter(
      (r) => r.status === "Resolved" || r.status === "Closed",
    ).length;
    return { total, open, inProg, resolved };
  }, [rows]);

  return (
    <>
      <Header />
      <div id="db-wrapper">
        <main id="page-content">
          <section className="container-fluid p-4 pt-3">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <div>
                <h3 className="mb-0 lh-1 ls-sm fw-normal">Support Tickets</h3>
                <div className="text-secondary lh-1">
                  Customer issues • Responses • Resolutions
                </div>
              </div>
              <div className="d-flex gap-1">
                <button
                  type="button"
                  className="btn btn-outline-secondary border-light px-4 text-dark bg-white"
                  onClick={() => {
                    setQ("");
                    setFilterStatus("");
                    setFilterPriority("");
                  }}
                >
                  <i className="bi bi-funnel me-2" />
                  Reset Filters
                </button>
                <button
                  type="button"
                  className="btn btn-light border px-4"
                  onClick={fetchRows}
                >
                  <i className="bi bi-arrow-clockwise me-2" />
                  Refresh
                </button>
              </div>
            </div>

            <div className="row g-2 mb-2">
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm rounded-4 h-100">
                  <div className="card-body p-3 d-flex align-items-center gap-2">
                    <div
                      className="rounded-3 d-inline-flex align-items-center justify-content-center bg-primary-subtle text-primary"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="bi bi-headset" />
                    </div>
                    <div>
                      <div className="text-secondary small lh-1">Total</div>
                      <div className="fs-5 fw-semibold lh-1 mt-1">
                        {stats.total}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm rounded-4 h-100">
                  <div className="card-body p-3 d-flex align-items-center gap-2">
                    <div
                      className="rounded-3 d-inline-flex align-items-center justify-content-center bg-warning-subtle text-warning"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="bi bi-envelope-exclamation" />
                    </div>
                    <div>
                      <div className="text-secondary small lh-1">Open</div>
                      <div className="fs-5 fw-semibold lh-1 mt-1">
                        {stats.open}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm rounded-4 h-100">
                  <div className="card-body p-3 d-flex align-items-center gap-2">
                    <div
                      className="rounded-3 d-inline-flex align-items-center justify-content-center bg-info-subtle text-info"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="bi bi-clock-history" />
                    </div>
                    <div>
                      <div className="text-secondary small lh-1">
                        In Progress
                      </div>
                      <div className="fs-5 fw-semibold lh-1 mt-1">
                        {stats.inProg}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm rounded-4 h-100">
                  <div className="card-body p-3 d-flex align-items-center gap-2">
                    <div
                      className="rounded-3 d-inline-flex align-items-center justify-content-center bg-success-subtle text-success"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="bi bi-check2-circle" />
                    </div>
                    <div>
                      <div className="text-secondary small lh-1">Resolved</div>
                      <div className="fs-5 fw-semibold lh-1 mt-1">
                        {stats.resolved}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm rounded-4">
              <div className="card-body p-3 p-md-4">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <div
                    className="rounded-3 d-inline-flex align-items-center justify-content-center bg-primary-subtle text-primary"
                    style={{ width: 40, height: 40 }}
                  >
                    <i className="bi bi-search" />
                  </div>
                  <div>
                    <div className="fw-semibold lh-1">Search & Filters</div>
                    <div className="text-secondary small">
                      Find by subject, customer name, or message
                    </div>
                  </div>
                </div>
                <div className="row g-2">
                  <div className="col-12 col-md-6">
                    <label className="form-label mb-1 fw-normal">Search</label>
                    <input
                      className="form-control"
                      placeholder="Subject / message / customer"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                    />
                  </div>
                  <div className="col-6 col-md-3">
                    <label className="form-label mb-1 fw-normal">Status</label>
                    <select
                      className="form-select"
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                    >
                      <option value="">All Status</option>
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-6 col-md-3">
                    <label className="form-label mb-1 fw-normal">
                      Priority
                    </label>
                    <select
                      className="form-select"
                      value={filterPriority}
                      onChange={(e) => setFilterPriority(e.target.value)}
                    >
                      <option value="">All Priorities</option>
                      {PRIORITIES.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {opened && (
              <div
                ref={editorRef}
                className="card border-0 shadow-sm rounded-4 mt-2"
              >
                <div className="card-body p-3 p-md-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="d-flex align-items-center gap-2">
                      <div
                        className="rounded-3 d-inline-flex align-items-center justify-content-center bg-success-subtle text-success"
                        style={{ width: 40, height: 40 }}
                      >
                        <i className="bi bi-chat-left-text" />
                      </div>
                      <div>
                        <div className="fw-semibold lh-1">{opened.subject}</div>
                        <div className="text-secondary small">
                          From {opened.customer?.name || "—"} •{" "}
                          {new Date(opened.createdAt).toLocaleString()}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-secondary border-light px-4 text-dark bg-white"
                      onClick={closeTicket}
                    >
                      <i className="bi bi-x-lg me-1" />
                      Close
                    </button>
                  </div>

                  <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                    <span className={`badge ${statusColor(opened.status)}`}>
                      {opened.status}
                    </span>
                    <span className={`badge ${priorityColor(opened.priority)}`}>
                      {opened.priority}
                    </span>
                    {opened.relatedOrder?.orderNumber && (
                      <span className="badge bg-secondary-subtle text-dark">
                        <i className="bi bi-receipt me-1" />
                        Order: {opened.relatedOrder.orderNumber}
                      </span>
                    )}
                    <span className="badge bg-light text-dark">
                      <i className="bi bi-telephone me-1" />
                      {opened.customer?.phone || "—"}
                    </span>
                    {opened.customer?.email && (
                      <span className="badge bg-light text-dark">
                        <i className="bi bi-envelope me-1" />
                        {opened.customer.email}
                      </span>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="form-label small text-secondary">
                      Customer Message
                    </label>
                    <div
                      className="p-3 bg-light rounded-3"
                      style={{ whiteSpace: "pre-wrap" }}
                    >
                      {opened.message}
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label mb-1 fw-normal">
                      Admin Response
                    </label>
                    <textarea
                      className="form-control"
                      rows="4"
                      value={response}
                      onChange={(e) => setResponse(e.target.value)}
                      placeholder="Reply to the customer..."
                    />
                    {opened.respondedAt && (
                      <small className="text-secondary">
                        Last responded:{" "}
                        {new Date(opened.respondedAt).toLocaleString()}
                      </small>
                    )}
                  </div>

                  <div className="row g-2">
                    <div className="col-md-6">
                      <label className="form-label mb-1 fw-normal">
                        Status
                      </label>
                      <select
                        className="form-select"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label mb-1 fw-normal">
                        Priority
                      </label>
                      <select
                        className="form-select"
                        value={priority}
                        onChange={(e) => setPriority(e.target.value)}
                      >
                        {PRIORITIES.map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="d-flex gap-2 mt-3 justify-content-end">
                    <button
                      type="button"
                      className="btn btn-light px-4"
                      onClick={closeTicket}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary px-4"
                      onClick={saveTicket}
                      disabled={saving}
                    >
                      {saving && (
                        <span className="spinner-border spinner-border-sm me-2" />
                      )}
                      {saving ? "Saving..." : "Save Ticket"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="card border-0 shadow-sm rounded-4 mt-2">
              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" />
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="text-center py-5 text-secondary">
                    <i className="bi bi-inbox fs-1 d-block mb-2 opacity-50" />
                    No tickets found
                  </div>
                ) : (
                  <div className="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                    <table className="table mb-0 text-nowrap table-centered">
                      <tbody>
                        {filtered.map((r, index) => (
                          <tr
                            key={r._id}
                            className={
                              opened?._id === r._id ? "table-active" : ""
                            }
                          >
                            <td>{index + 1}.</td>
                            <td className="lh-sm py-4 text-wrap">
                              <span className="text-dark">Customer</span>{" "}
                              &mdash; {r.customer?.name || "—"}
                              <br />
                              <span className="text-dark">
                                Phone
                              </span> &mdash; {r.customer?.phone || "—"}
                              <br />
                              <span className="text-dark">
                                Subject
                              </span> &mdash; {r.subject}
                              <br />
                              <span className="text-dark">
                                Order
                              </span> &mdash;{" "}
                              {r.relatedOrder?.orderNumber
                                ? r.relatedOrder.orderNumber
                                : "—"}
                              <br />
                              <span className="text-dark">Priority</span>{" "}
                              &mdash;{" "}
                              <span
                                className={`badge ${priorityColor(r.priority)}`}
                              >
                                {r.priority}
                              </span>
                              <br />
                              <span className="text-dark">
                                Status
                              </span> &mdash;{" "}
                              <span
                                className={`badge ${statusColor(r.status)}`}
                              >
                                {r.status}
                              </span>
                              <br />
                              <span className="text-dark">
                                Created
                              </span> &mdash;{" "}
                              <span className="text-secondary">
                                {new Date(r.createdAt).toLocaleString()}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-1 align-items-start">
                                <span
                                  onClick={() => openTicket(r)}
                                  className="badge bg-primary-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  <i className="bi bi-eye me-1" />
                                  Open
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </section>
        </main>
      </div>
    </>
  );
}

export default SupportTickets;
